import React, { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { ImageIcon } from 'lucide-react';
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import { useTranslation } from 'react-i18next';

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

const ImageGallery = ({ imageGallery }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  if (!imageGallery?.images_path?.length) {
    return (
      <Box className="lot-image">
        <Box className="no-image-placeholder">
          <ImageIcon className="w-12 h-12 text-gray-400" />
          <Box className="text-sm text-gray-500">{t('item.noImage')}</Box>
        </Box>
      </Box>
    );
  }

  const slides = imageGallery.images_path.map(image => ({
    src: image.zoom,
    thumbnail: image.small,
    download: image.zoom
  }));

  const hasMultipleImages = imageGallery.images_path.length > 1;

  return (
    <div className="lot-image">
      {/* Main Image */}
      <Box 
        className="my-gallery" 
        itemScope 
        itemType="http://schema.org/ImageGallery"
      >
        <figure 
          itemProp="associatedMedia" 
          itemScope 
          itemType="http://schema.org/ImageObject"
          data-image-id={`main-${imageGallery.images_path[currentImageIndex].id}`}
          className="current-zoomed-image active"
        >
          <img
            src={imageGallery.images_path[currentImageIndex].big}
            alt={`Gallery image ${currentImageIndex + 1}`}
            style={{ objectFit: 'contain', height: '100%', width: '100%', cursor: 'zoom-in' }}
            onClick={() => setIsOpen(true)}
          />
        </figure>
      </Box>

      {/* Thumbnails */}
      {hasMultipleImages && (
        <div className="lot-image-thumbs" >
            {imageGallery.images_path.map((image, index) => (
              <img
                key={image.id_thumb}
                src={image.small}
                alt={`Thumbnail ${index + 1}`}
                className={currentImageIndex === index ? 'active' : ''}
                id={`thumb-${image.id_thumb}`}
                data-nid={image.id}
                data-big-id={`main-${image.id}`}
                onClick={() => setCurrentImageIndex(index)}
                style={{ 
                  cursor: 'pointer',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
              />
            ))}
          
        </div>
      )}

      {/* Images Count */}
      {hasMultipleImages && (
        <div className="images-count">
          <small>
            {imageGallery.images_path.length} {t('item.photos')}
          </small>
        </div>
      )}

      {/* Lightbox */}
      {isOpen && (
        <Lightbox
          open={isOpen}
          close={() => setIsOpen(false)}
          index={currentImageIndex}
          slides={slides}
          plugins={[Zoom, Counter, Download, Thumbnails]}
          counter={{
            container: { 
              style: { 
                top: 'unset', 
                bottom: 0, 
                left: 0,
                padding: '8px 12px',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                borderRadius: '4px',
                margin: '8px'
              } 
            }
          }}
          zoom={{
            maxZoomPixelRatio: 4,
            zoomInMultiplier: 2,
            doubleTapDelay: 300,
            scrollToZoom: true
          }}
          thumbnails={{
            position: "bottom",
            width: 120,
            height: 80,
            border: 2,
            borderRadius: 4,
            gap: 16,
            padding: 4
          }}
          carousel={{ 
            finite: true,
            preload: 3
          }}
          styles={{
            container: { backgroundColor: "rgba(0, 0, 0, .9)" },
            thumbnail: { 
              border: "2px solid transparent",
              transition: "border-color 0.3s",
              "&.yarl__thumbnail_active": {
                border: "2px solid #3182ce"
              }
            }
          }}
          download={{
            downloadText: "Download Image"
          }}
          controller={{
            closeOnBackdropClick: true,
            closeOnPullDown: true
          }}
        />
      )}
    </div>
  );
};

export default ImageGallery;